'use client';

// Imports the global styles for the site
import { ApolloProvider, gql, useQuery } from '@apollo/client';
import { PermissionName } from '@kg-internal/pv3-helpers';
import dayjs from 'dayjs';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import React, { createContext, JSX, useContext, useEffect, useState } from 'react';
import Commander from '../../components/Commander/Commander';
import ErrorDisplay from '../../components/ErrorDisplay/ErrorDisplay';
import LogoLoader from '../../components/LogoLoader/LogoLoader';
import NoPermission from '../../components/NoPermission/NoPermission';
import config, { PortalConfigType } from '../../config';
// import '../../styles/global.scss';
import graphClient, { baseLink, graphClientPersisted, persistedLink, simpleClient, simpleLink } from './graphClient';
import { getClosestOffice, getCurrentLocation, locations, TLocation } from './locationsUtil';
import userObj, { clientPermissionLevel, IUserObj } from './userObj';

type envType = 'production' | 'development' | 'staging' | null;

// Redirect to login functions
const redirectToLogin = (env: envType) => {
  const { loginUrl } = env ? (config.urls[env] ?? config.urls) : config.urls;
  const prevLocation = encodeURI(window.location.href);

  // ensure loading screen is visible before redirect occurs
  setTimeout(() => {
    console.info(`redirecting to: ${loginUrl}/login?returnUrl=${prevLocation}`);
    window.location.href = `${loginUrl}/login?returnUrl=${prevLocation}`;
  }, 5);
};

// Redirect to handbook because the person has updates
const redirectToHandbook = (env: envType) => {
  const { handbook } = env ? (config.urls[env] ?? config.urls) : config.urls;
  const prevLocation = encodeURI(window.location.href);

  // ensure loading screen is visible before redirect occurs
  setTimeout(() => {
    window.location.href = `${handbook}/?returnUrl=${prevLocation}`;
  }, 5);
};

const redirectToEditProfile = (env: envType, id: number) => {
  const { directory } = env ? (config.urls[env] ?? config.urls) : config.urls;

  // ensure loading screen is visible before redirect occurs
  setTimeout(() => {
    window.location.href = `${directory}/profile/${id}/edit`;
  }, 5);
};
// Redirect to handbook because the person has updates
const redirectToTimesheet = (env: envType) => {
  const { timetracker } = env ? (config.urls[env] ?? config.urls) : config.urls;

  const lastWeek = dayjs().subtract(1, 'week').day(1).format('YYYY-MM-DD');

  // ensure loading screen is visible before redirect occurs
  setTimeout(() => {
    window.location.href = `${timetracker}/week/${lastWeek}?incomplete=true`;
  }, 5);
};

interface IApolloContextLite {
  children: React.ReactNode | Array<React.ReactNode>;
  name: string;
  version: string;
  persistedQueries?: boolean;
  simple?: boolean;
}

// Apollo context to provide API access
export const ApolloContextLite = (props: IApolloContextLite) => {
  const { children, name, version, simple = false, persistedQueries = true } = props;

  if (name) {
    // @ts-expect-error there is no other way to access this property
    graphClient.queryManager.clientAwareness.name = name;
  }

  if (version) {
    // @ts-expect-error there is no other way to access this property
    graphClient.queryManager.clientAwareness.version = version;
  }

  if (simple) {
    graphClient.link = simpleLink;
  } else if (persistedQueries) {
    graphClient.link = persistedLink;
  } else {
    graphClient.link = baseLink;
  }

  return <ApolloProvider client={graphClient}>{children}</ApolloProvider>;
};

interface IApolloContext extends IPortalContext {
  children: React.ReactNode | Array<React.ReactNode>;
  name: string;
  version: string;
  persistedQueries?: boolean;
  simple?: boolean;
}

// Apollo context rto provide API access
const ApolloContext = (props: IApolloContext) => {
  const { children, name, version, simple = false, persistedQueries = true, ...otherProps } = props;

  if (name) {
    // @ts-expect-error there is no other way to access this property
    simpleClient.queryManager.clientAwareness.name = name;
    // @ts-expect-error there is no other way to access this property
    graphClient.queryManager.clientAwareness.name = name;
    // @ts-expect-error there is no other way to access this property
    graphClientPersisted.queryManager.clientAwareness.name = name;
  }

  if (version) {
    // @ts-expect-error there is no other way to access this property
    simpleClient.queryManager.clientAwareness.version = version;
    // @ts-expect-error there is no other way to access this property
    graphClient.queryManager.clientAwareness.version = version;
    // @ts-expect-error there is no other way to access this property
    graphClientPersisted.queryManager.clientAwareness.version = version;
  }

  return (
    <ApolloProvider client={simple ? simpleClient : persistedQueries ? graphClientPersisted : graphClient}>
      <PostHogProvider
        apiKey={
          props.env === 'production'
            ? 'phc_70C1xiKrZbavrOEBgDLsgfTJIkEPjrhEnt5ZvhGZxLQ' // prod
            : 'phc_MzEfTBvkPfqsrIo3tFTDGyqWdNh8bVl2PxetPiJiCX5' // stage
        }
        options={{ api_host: 'https://app.posthog.com' }}>
        <PortalContext {...otherProps} name={name} version={version}>
          {children}
        </PortalContext>
      </PostHogProvider>
    </ApolloProvider>
  );
};

// Base context so we can layer in user details
const BaseCtx = createContext({} as IUserObj);

const USER_ME = gql`
  query PortalContextMe {
    me {
      id
      email
      nameFirst
      nameLast
      jobTitle
      hasHandbookUpdates
      hasFilledProfile
      hasTimesheetDue
      photoUrl(size: small)
      isPartTime
      isManager
      manager {
        id
        nameFirst
        nameLast
        email
      }
      tracksPTO
      office {
        OfficeID
        Name
      }
      department {
        id
        DepartmentID
        Name
      }
      permissions {
        id
        name
      }
      accountServicesTeams {
        id
        permissionType
        projectAccountID
      }
    }
    featureFlagListV2 {
      id
      name
      description
      active
    }
  }
`;

interface IPortalContext {
  children: React.ReactNode | Array<React.ReactNode>;
  env: envType;
  name: string;
  version: string;
  permissions?: Array<PermissionName>;
  bypassHandbookCheck?: boolean; // for use on the handbook itself
  bypassEditProfileCheck?: boolean; // for use on the profile itself
  bypassTimesheetCheck?: boolean; // for use on the timesheet itself
}

const cookieMe = localStorage?.getItem('portalMe');
const cookieMeObjInt = cookieMe ? JSON.parse(cookieMe) : null;

const cookieMeObj = //@ts-ignore
  cookieMeObjInt && cookieMeObjInt.v === 1 && cookieMeObjInt.exp > dayjs().unix() ? cookieMeObjInt : null;

const PortalContext = (props: IPortalContext) => {
  const posthog = usePostHog();

  if (props.env && props.env !== 'production') {
    console.info('This application is running in env mode: ' + props.env);
  }

  const { loading, error, data } = useQuery(USER_ME, {
    onCompleted: (data) => {
      if (data?.me) {
        const tomorrow = dayjs().add(1, 'day').unix();
        localStorage?.setItem(
          'portalMe',
          JSON.stringify({ me: data.me, featureFlagListV2: data.featureFlagListV2, exp: tomorrow, v: 1 }),
        );
      }
    },
  });

  useEffect(() => {
    if (data) {
      // Identify sends an event, so you want may want to limit how often you call it
      posthog?.identify(`${data.me.id}`, {
        email: data.me.email,
        name: `${data.me.nameFirst} ${data.me.nameLast}`,
      });
      posthog?.group('department', data.me.department.id);
      posthog?.group('office', data.me.office.OfficeID);
    }
  }, [posthog, data?.me.id]);

  if (error) {
    // @ts-expect-error the type returned from apollo is not correct
    const isAuthError = error.networkError?.statusCode === 401;
    // @ts-expect-error: apollo router attaches status to error.extensions which is an any type
    const isApolloRouterAuthError = error?.graphQLErrors.some((error) => error?.extensions?.http?.status === 401);
    if (isAuthError || isApolloRouterAuthError) {
      redirectToLogin(props.env);
      return <LogoLoader />;
    } else {
      console.error(error);
      console.error(error.networkError);
      // @ts-expect-error the type returned from apollo is not correct
      return <ErrorDisplay title={`Server Error: ${error.networkError?.statusCode}`} error={error} />;
    }
  }

  if (!loading || cookieMeObj) {
    // Loading is complete
    // TODO: stop infinite auth loop and show perm error after number of retries
    if ((!data?.me || !data?.me.email) && !loading) {
      // User is not logged in; move them to them to the login page
      redirectToLogin(props.env);
      return <LogoLoader />;
    } else if (!props.bypassHandbookCheck && data?.me?.hasHandbookUpdates) {
      redirectToHandbook(props.env);
      return <LogoLoader />;
    } else if (!props.bypassEditProfileCheck && data?.me?.hasFilledProfile === false) {
      redirectToEditProfile(props.env, data.me.id);
    } else if (!props.bypassTimesheetCheck && data?.me?.hasTimesheetDue) {
      redirectToTimesheet(props.env);
      return <LogoLoader />;
    }
  } else {
    return <LogoLoader />;
  }

  //Set the config for the user
  const finalConfig = { ...config } as PortalConfigType;

  finalConfig.urls = props.env ? (config.urls[props.env] ?? config.urls) : config.urls;

  const ctxUser = userObj(data || cookieMeObj, props.env || 'production', finalConfig, props.name, props.version);

  // User must have permission to view the app
  if (props.permissions) {
    const url = ctxUser.config.urls.portal ?? 'https://kgportal.com';

    if (!ctxUser.hasPermissions(props.permissions)) {
      return <NoPermission href={url} />;
    }
  }

  return (
    <BaseCtx.Provider value={ctxUser}>
      <Commander />
      {props.children}
    </BaseCtx.Provider>
  );
};

export default ApolloContext;

export const useUser = () => {
  return useContext(BaseCtx);
};

export const useEnv = () => {
  const usr = useContext(BaseCtx);

  return usr.env;
};

export const useConfig = (): PortalConfigType => {
  const usr = useContext(BaseCtx);

  if (!usr || !usr.config) {
    return config;
  }

  return usr.config;
};

// Used to validate specific permissions against the user
export const usePermissions = (permissionNames: PermissionName | Array<PermissionName>, allRequired?: boolean) => {
  const usr = useContext(BaseCtx);
  const [hasPerm, setHasPerm] = useState(false);

  useEffect(() => {
    if (usr && usr.hasPermissions) {
      setHasPerm(usr.hasPermissions(permissionNames, allRequired));
    }
  }, [usr, permissionNames, allRequired]);

  return hasPerm;
};

export const useClientPermissions = (
  accountId: number | undefined,
  permissionLevels?: clientPermissionLevel | Array<clientPermissionLevel>,
) => {
  const usr = useContext(BaseCtx);
  const [hasClientPerm, setHasClientPerm] = useState(false);

  useEffect(() => {
    if (usr.hasPermissions('crm.admin', true)) {
      setHasClientPerm(true);
    } else if (accountId === undefined) {
      setHasClientPerm(false);
    } else if (usr) {
      setHasClientPerm(usr.hasClientPermissions(accountId, permissionLevels));
    }
  }, [usr, permissionLevels, accountId]);

  return hasClientPerm;
};

interface IPermissions {
  children: JSX.Element | Array<JSX.Element>;
  name: PermissionName | Array<PermissionName>;
  all?: boolean | undefined;
  not?: boolean | undefined;
  hide?: boolean | undefined;
}

// Provides a helper for hiding/showing based on permission
export const Permission = (props: IPermissions): JSX.Element => {
  const flg = usePermissions(props.name, props.all);
  if (props.hide) {
    return <></>;
  }

  if (props.not) {
    return flg ? <></> : <>{props.children}</>;
  }

  return flg ? <>{props.children}</> : <></>;
};

interface IClientPermissions {
  children: JSX.Element | Array<JSX.Element>;
  accountId: number | undefined;
  permissionLevels: clientPermissionLevel | Array<clientPermissionLevel>;
  hide?: boolean | undefined;
}

// Helper for hiding/showing based on Client Permissions
export const ClientPermission = (props: IClientPermissions): JSX.Element => {
  let flg = false;
  const usr = useContext(BaseCtx);

  if (props.accountId || usr.hasPermissions('crm.admin', true)) {
    flg = useClientPermissions(props.accountId, props.permissionLevels);
  }

  if (props.hide) {
    return <></>;
  }

  return flg ? <>{props.children}</> : <></>;
};

/**
 *
 * @param {string} featureFlagName The name of the feature flag that you want to use.
 * @param {Object} allRequired The current context feature flags.
 * @returns {Boolean} `true` If the current user/context has the feature flag enabled, `false` if not
 */
export const useFeatureFlag = (featureFlagName: string | Array<string>, allRequired?: boolean) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const usr = useContext(BaseCtx);

  useEffect(() => {
    setIsEnabled(usr.hasFlags(featureFlagName, allRequired));
  }, [usr, featureFlagName, allRequired]);

  return isEnabled;
};

interface IFeatureFlag {
  children: React.ReactNode | Array<React.ReactNode>;
  name: string | Array<string>;
  all?: boolean | undefined;
  not?: boolean | undefined;
  hide?: boolean | undefined;
}

// Feature flag helper wrapper
export const FeatureFlag = (props: IFeatureFlag) => {
  const flg = useFeatureFlag(props.name, props.all);

  if (props.hide) {
    return <></>;
  }

  if (props.not) {
    return flg ? <></> : <>{props.children}</>;
  }

  return flg ? <>{props.children}</> : <></>;
};

export const useUserLocation = (geoLookup) => {
  const usr = useUser();
  const knownOffice = usr && usr.details
    ? locations.find((location) => location.OfficeID === usr?.details?.office?.OfficeID)
    : null;
  const homeOffice: TLocation | null = knownOffice ?? (
    usr?.details?.office?.OfficeID
      ? { Name: usr.details.office.Name ?? '', OfficeID: usr.details.office.OfficeID, latitude: 0, longitude: 0 }
      : null
  );
  const [location, setLocation] = useState(null as TLocation | null);

  function getLocation() {
    getCurrentLocation()
      .then((position: any) => {
        const geoOffice = getClosestOffice(position.coords.latitude, position.coords.longitude);

        setLocation(geoOffice);
      })
      .catch((err) => {
        // will go here if gelocation is disabled by user
        console.warn('could not geolocate user', err);
      });
  }

  useEffect(() => {
    if (geoLookup) {
      getLocation();
    }
  }, [geoLookup]); // In case our needs change

  return location || homeOffice; // Using the or, becausse an actual location beats thie home office
};
